import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Grid,
  Backdrop,
  CircularProgress,
  Chip,
} from "@mui/material";
import { formatDateWithTimeAMPM } from "@/shared/utils/format-date";

/**
 * GenericObjectModal
 * 
 * Renderiza un objeto en un modal a partir de un arreglo de claves.
 * - Acepta claves simples o anidadas con notación de puntos (ej. "cliente.telefono").
 * - Permite labels y formateadores opcionales por campo.
 * - Muestra fallback cuando el valor es null/undefined/"".
 * 
 */

export type ArrayRenderMode = "chips" | "list" | "table";

export type FieldSpec<T extends Record<string, any>> =
  | string
  | {
    key: string; // permite dot-path
    label?: string;
    formatter?: (value: any, full: T) => React.ReactNode;
    hideIfEmpty?: boolean; // si true y value es vacío, no se renderiza la fila
    fullWidth?: boolean;
  };

export type GenericObjectModalProps<T extends Record<string, any>> = {
  open: boolean;
  onClose: () => void;
  data: T | null | undefined;
  fields: FieldSpec<T>[];
  title?: string;
  emptyFallback?: string; // default: "N/A"
  twoColumns?: boolean;
  isLoading: boolean
};

function isEmptyValue(v: any) {
  return v === null || v === undefined || (typeof v === "string" && v.trim() === "");
}

function getByPath(obj: any, path: string) {
  return path.split(".").reduce((acc, k) => (acc != null ? acc[k] : undefined), obj);
}

// ==== Boolean helpers (auto-render Sí/No) ====
const isBooleanLike = (x: any) => {
  if (x === true || x === false) return true;
  return false;
};

const renderBooleanDefault = (x: any) => {
  const truthy = x === true;
  return (
    <Chip
      size="small"
      label={truthy ? "Sí" : "No"}
      color={truthy ? "success" : "default"}
      variant={truthy ? "filled" : "outlined"}
    />
  );
};

// ==== Date helpers (auto-render YYYY-MM-DD) ====
const isBackendIsoLike = (x: any) =>
  typeof x === "string" &&
  /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.\d+)?([+-]\d{2}:\d{2}|Z)$/.test(x);

const renderDateDefault = (x: any) => {
  const dateFormat = formatDateWithTimeAMPM(x);
  return String(dateFormat);
};


export default function ModalGenericObject<T extends Record<string, any>>({
  open,
  onClose,
  data,
  fields,
  title = "Detalles",
  emptyFallback = "N/A",
  twoColumns = true,
  isLoading
}: GenericObjectModalProps<T>) {
  const resolvedFields = React.useMemo(() =>
    fields.map((f) => (typeof f === "string" ? { key: f } : f)),
    [fields]);

  const autoTwoCols = twoColumns ?? resolvedFields.length >= 8;

  return (
    <>
      <Backdrop open={isLoading} sx={{ zIndex: (t) => t.zIndex.modal + 1 }}>
        <CircularProgress />
      </Backdrop>

      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {!data ? (
            <Typography color="text.secondary">Sin datos.</Typography>
          ) : (
            <Grid container spacing={1} columns={autoTwoCols ? 12 : 1}>
              {resolvedFields.map((f, idx) => {
                const raw = getByPath(data, f.key);
                const isEmpty = isEmptyValue(raw);
                if (f.hideIfEmpty && isEmpty) return null;

                const valueNode = isEmpty
                  ? emptyFallback
                  : f.formatter
                    ? f.formatter(raw, data)
                    : isBackendIsoLike(raw)
                      ? renderDateDefault(raw)
                      : isBooleanLike(raw)
                        ? renderBooleanDefault(raw)
                        : String(raw);

                const label = f.label ?? f.key;

                const fullWidth = f.fullWidth === true;
                
                const colProps = autoTwoCols
                  ? (fullWidth
                    ? { xs: 12, sm: 12, md: 12 }
                    : { xs: 12, sm: 6, md: 6 })
                  : { xs: 12 };

                return (
                  <Grid key={`${f.key}-${idx}`} {...colProps}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{
                        p: 0.5,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            mt: 0.25,
                            fontWeight: 600,
                            wordBreak: "break-word",
                          }}
                        >
                          {valueNode}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}